import { getItemCoverageData, getCoverageData } from "@/api/coverage";
import { TYPE_MAPPER_CHARTS, COVERAGE_CHART_DEFINITION } from "@/misc/desktopCharts";
import { subDays, startOfDay, endOfDay } from "date-fns";
import { getEndOfValidDate } from "@/lib/utilFunctions";

const state = {
  coverageData: {},
  itemCoverageData: {},
  itemCoverageMonthDate: null,
  coverageDateRange: {},
};

const getters = {
  getItemTotals: (state) => state.itemCoverageData.tm_status,
  getItemMonthlyTotals: (state) => state.itemCoverageData.tm_monthly_status,
  getItemDetails: (state) => state.itemCoverageData.tm_latest_items,
  getDownloadItems: (state) => state.itemCoverageData.download_items,
  getItemDate: (state) => state.itemCoverageMonthDate,
  getCoverageDateRange: (state) => state.coverageDateRange,
  getCoverageData: (state) => state.coverageData,
};

const actions = {
  initDateRange({ dispatch }) {
    const endOfValidDate = getEndOfValidDate();
    dispatch("setCoverageDateRange", {
      startDate: startOfDay(subDays(endOfValidDate, 30)),
      endDate: startOfDay(endOfValidDate),
    });
  },
  async fetchCurrentItemCoverageData({ commit, getters, rootGetters }, chartName) {
    const targetDate = getters["getItemDate"];
    const currentStore = rootGetters["user/getCurrentStore"];
    const context = "itemCoverageData";
    commit("setLoadingStatus", { isLoading: true, chartName, context });
    try {
      let data = await getItemCoverageData(chartName, targetDate, currentStore.store_id);
      commit("setData", { data, chartName, context });
    } catch (e) {
      commit("setError", { chartName, context });
    } finally {
      commit("setLoadingStatus", { isLoading: false, chartName, context });
    }
  },
  async fetchCurrentCoverageData({ commit, state, rootGetters }, chartName) {
    const startDate = startOfDay(subDays(state.coverageDateRange.startDate, 1));
    const endDate = endOfDay(subDays(state.coverageDateRange.endDate, 1));
    const currentStore = rootGetters["user/getCurrentStore"];
    const filters = rootGetters["filters/getAllActiveFilters"];
    const context = "coverageData";
    commit("setLoadingStatus", { isLoading: true, chartName, context });
    try {
      let data = await getCoverageData({ chartName, startDate, endDate, storeId: currentStore.store_id, filters });
      commit("setData", { data, chartName, context });
    } catch (e) {
      commit("setError", { chartName, context });
    } finally {
      commit("setLoadingStatus", { isLoading: false, chartName, context });
    }
  },
  setCurrentItemCoverageData({ dispatch }) {
    TYPE_MAPPER_CHARTS.forEach((chartName) => dispatch("fetchCurrentItemCoverageData", chartName));
  },
  setCurrentCoverageData({ dispatch }) {
    COVERAGE_CHART_DEFINITION.forEach((definition) => dispatch("fetchCurrentCoverageData", definition.name));
  },
  setItemCoverageMonthDate({ dispatch, commit }, date) {
    commit("setItemCoverageMonthDate", date);
    dispatch("setCurrentItemCoverageData");
  },
  setCoverageDateRange({ dispatch, commit }, payload) {
    commit("setCoverageDateRange", payload);
    dispatch("setCurrentCoverageData");
  },
};

const mutations = {
  setLoadingStatus(state, payload) {
    const { isLoading, chartName, context } = payload;
    state[context] = {
      ...state[context],
      [chartName]: { ...state[context][chartName], isLoading },
    };
  },
  setData(state, payload) {
    const { data, chartName, context } = payload;
    const isError = !!data.isError;

    state[context] = {
      ...state[context],
      [chartName]: {
        ...state[context][chartName],
        ...data,
        isError,
      },
    };
  },
  setError(state, payload) {
    const { chartName, context } = payload;
    state[context] = {
      ...state[context],
      [chartName]: { ...state[context][chartName], isError: true },
    };
  },
  setItemCoverageMonthDate(state, payload) {
    state.itemCoverageMonthDate = payload;
  },
  setCoverageDateRange(state, payload) {
    state.coverageDateRange = {};
    state.coverageDateRange.startDate = payload.startDate;
    state.coverageDateRange.endDate = payload.endDate;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
